import { useEffect, useMemo, useState } from "react";
import { API_URL } from "@/lib/config";
import { parseAppPath } from "@/lib/app-route";
import { Button } from "@/components/ui/button";
import { setPendingAutoJoin } from "@/lib/pending-auto-join";
import {
  fetchCommunityPreview,
  fetchPublicContributors,
  fetchPublicChannelContent,
  type PublicWorkspaceMetadata,
  type CommunityPreview,
  type PublicContributor,
  type PublicChannelContent,
} from "@ano/shared/api/public-workspaces";
import {
  trackPublicPageViewed,
  trackPublicPageCtaClicked,
  trackPublicPreviewLoaded,
} from "@/lib/analytics-events";
import { buildCommunityStats } from "./PublicWorkspaceSplash.parts";
import {
  AnonymousHeader,
  ChannelFocusSection,
  CommunityHero,
  CommunityRulesSection,
  ConversionBlock,
  InsidePreviewSection,
  TopContributorsSection,
  resolvePreviewOutcome,
} from "./PublicWorkspaceAnonymousView.parts";

const ANONYMOUS_PREVIEW_TIMEOUT_MS = 8000;

// Phase 4 live polling — re-fetch the focused channel + preview on this cadence
// so the page refreshes new redacted messages without a manual reload. Paused
// while the tab is hidden (no point burning the public endpoint in background
// tabs).
const LIVE_POLL_INTERVAL_MS = 12_000;

interface PublicWorkspaceAnonymousViewProps {
  /** Already-fetched public metadata (the owner enabled `anonymous_view`). */
  meta: PublicWorkspaceMetadata;
  /** Start the WorkOS sign-in flow (returnTo is the current URL). */
  onSignIn: () => void;
  /** Start the WorkOS sign-up flow. */
  onSignUp: () => void;
  /** One-click GitHub sign-up (deep-links to the WorkOS GitHub connection). */
  onContinueWithGithub: () => void;
}

/**
 * Logged-OUT public-workspace landing (Phase 5).
 *
 * Shown by `AuthGate` — BEFORE the sign-in wall — when an unauthenticated
 * visitor lands on a public-workspace URL (path `/acme` or legacy
 * `acme.ano.dev`) whose owner enabled `community_settings.anonymous_view`.
 * Lets a stranger read a real,
 * server-redacted slice of the community (tagline, rules, featured channels +
 * recent messages) so they convert into a sign-up.
 *
 * REST-ONLY by construction: this renders OUTSIDE `<ZeroProvider>` and has no
 * auth token, so it talks only to the unauthenticated `/public` + `/preview`
 * endpoints (server-redacted to featured PUBLIC channels). It never touches
 * Zero, mutators, or `useApiClient`.
 */
export function PublicWorkspaceAnonymousView({
  meta,
  onSignIn,
  onSignUp,
  onContinueWithGithub,
}: PublicWorkspaceAnonymousViewProps) {
  const [preview, setPreview] = useState<CommunityPreview | null>(null);
  const [previewStatus, setPreviewStatus] = useState<
    "loading" | "loaded" | "empty"
  >("loading");
  const [contributors, setContributors] = useState<PublicContributor[]>([]);

  // Deep-link: when the URL is `/acme/c/<channel>` (or legacy
  // `acme.ano.dev/c/<channel>`) — e.g. a Google result for the per-channel SEO
  // page — focus that channel so the human sees the SAME content the crawler
  // indexed — no soft-404 / cloaking mismatch.
  const channelName = useMemo(() => parseAppPath().channelName, []);
  const [channel, setChannel] = useState<PublicChannelContent | null>(null);

  useEffect(() => {
    if (!channelName) return;
    let cancelled = false;
    void fetchPublicChannelContent({
      apiBaseUrl: API_URL,
      slug: meta.slug,
      channelName,
    })
      .then((res) => {
        if (!cancelled) setChannel(res);
      })
      .catch(() => {
        /* fall back to the community overview */
      });
    return () => {
      cancelled = true;
    };
  }, [channelName, meta.slug]);

  useEffect(() => {
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      if (cancelled) return;
      setPreviewStatus("empty");
      trackPublicPreviewLoaded({
        slug: meta.slug,
        status: "timeout",
        channelCount: 0,
      });
    }, ANONYMOUS_PREVIEW_TIMEOUT_MS);
    void fetchCommunityPreview({ apiBaseUrl: API_URL, slug: meta.slug })
      .then((res) => {
        window.clearTimeout(timeout);
        if (cancelled) return;
        const { status, channelCount } = resolvePreviewOutcome(res);
        if (status === "loaded" && res) setPreview(res);
        setPreviewStatus(status);
        trackPublicPreviewLoaded({ slug: meta.slug, status, channelCount });
      })
      .catch(() => {
        window.clearTimeout(timeout);
        if (cancelled) return;
        setPreviewStatus("empty");
        // Distinguish a fetch failure from a genuinely empty community so the
        // dashboard doesn't read network errors as "no content yet".
        trackPublicPreviewLoaded({
          slug: meta.slug,
          status: "error",
          channelCount: 0,
        });
      });
    void fetchPublicContributors({ apiBaseUrl: API_URL, slug: meta.slug })
      .then((res) => {
        if (!cancelled && res) setContributors(res.contributors);
      })
      .catch(() => {
        /* contributors are a nice-to-have — ignore failures */
      });
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [meta.slug]);

  // Phase 4 live polling — keep the focused channel + the "Inside" preview
  // fresh without a reload. Refetches every `LIVE_POLL_INTERVAL_MS`, skips the
  // tick while the tab is hidden, and merges the latest redacted slice in. New
  // messages flow in by replacing the fetched content (the server already
  // returns the recent window), so there's no client-side dedupe to maintain.
  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      if (cancelled || document.visibilityState !== "visible") return;
      if (channelName) {
        void fetchPublicChannelContent({
          apiBaseUrl: API_URL,
          slug: meta.slug,
          channelName,
        })
          .then((res) => {
            if (!cancelled && res) setChannel(res);
          })
          .catch(() => {
            /* keep the last good content on a transient failure */
          });
      }
      void fetchCommunityPreview({ apiBaseUrl: API_URL, slug: meta.slug })
        .then((res) => {
          if (cancelled) return;
          const { status } = resolvePreviewOutcome(res);
          if (status === "loaded" && res) {
            setPreview(res);
            setPreviewStatus("loaded");
          }
        })
        .catch(() => {
          /* keep the last good preview on a transient failure */
        });
    };
    const interval = window.setInterval(tick, LIVE_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [channelName, meta.slug]);

  // Funnel top: a logged-out "lurker" landed on this public community page.
  // Fires once per landing (meta.slug is this view's stable identity) and
  // binds the workspace group so the visitor's events roll up to the community.
  useEffect(() => {
    trackPublicPageViewed({
      slug: meta.slug,
      workspaceId: meta.workspace_id,
      isChannelDeeplink: !!channelName,
      channelName,
      memberCount: meta.member_count,
      channelCount: meta.channel_count,
      entryMode: meta.entry_mode,
    });
    // Fire-once per slug; the other meta fields are stable for a given landing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.slug]);

  // Member count is intentionally omitted on the public lurking page — a
  // brand-new community reading "1 member" signals "dead", which suppresses
  // sign-ups. Channel count stays (it advertises content, not emptiness).
  const stats = useMemo(
    () => buildCommunityStats({ channelCount: meta.channel_count }),
    [meta.channel_count],
  );

  // Track the CTA click (with placement) AND invoke the original handler — the
  // handler also fires `auth intent` in AuthGate just before the OAuth hop.
  const handleCta = (
    action: "signup" | "signin",
    placement: "header" | "conversion_block",
  ) => {
    trackPublicPageCtaClicked({ slug: meta.slug, action, placement });
    if (action === "signup") {
      // Signing up from here = intent to join → auto-join after auth.
      if (meta.public_join_policy === "auto") setPendingAutoJoin(meta.slug);
      onSignUp();
    } else onSignIn();
  };

  const handleGithub = () => {
    trackPublicPageCtaClicked({
      slug: meta.slug,
      action: "signup",
      placement: "header",
    });
    if (meta.public_join_policy === "auto") setPendingAutoJoin(meta.slug);
    onContinueWithGithub();
  };

  return (
    <div className="flex h-screen flex-col bg-background">
      <AnonymousHeader meta={meta} onCta={handleCta} />

      <main className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto px-4 py-8 sm:px-6">
        <CommunityHero meta={meta} stats={stats} />

        <div className="mt-6 flex flex-col items-center gap-2">
          <Button size="lg" onClick={handleGithub} className="w-full sm:w-auto">
            Continue with GitHub
          </Button>
          <p className="text-xs text-muted-foreground">
            {meta.public_join_policy === "auto"
              ? "Join with GitHub to ask and answer — takes a few seconds."
              : "Sign up with GitHub to request access — takes a few seconds."}
          </p>
        </div>

        {channelName && channel && (
          <ChannelFocusSection
            channel={channel}
            workspaceName={meta.name}
            slug={meta.slug}
            autoJoin={meta.public_join_policy === "auto"}
            onContinueWithGithub={handleGithub}
          />
        )}

        <InsidePreviewSection
          workspaceName={meta.name}
          status={previewStatus}
          preview={preview}
        />

        {contributors.length > 0 && (
          <TopContributorsSection contributors={contributors} />
        )}

        {meta.rules && <CommunityRulesSection rules={meta.rules} />}

        <ConversionBlock
          workspaceName={meta.name}
          publicJoinPolicy={meta.public_join_policy}
          onCta={handleCta}
        />

        <p className="mt-8 text-center text-xs text-muted-foreground">
          Powered by Ano
        </p>
      </main>
    </div>
  );
}
